import { useEffect, useMemo, useRef, useState } from "react";

import {
  flexRender,
  getCoreRowModel,
  type PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

import {
  Button,
  DateRangePicker,
  Input,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@pbz/ui-kit";

import { columns } from "@/components/leads/columns";
import { useDebounce } from "@/hooks/useDebounce";
import { useGetLeadTabCount, useListLeads } from "@/queries/lead";
import { LEADS_PER_PAGE } from "@/utils/constants";

// Parse lead IDs from search term
const leadIdRegex = /[,\s]+/;

const LeadsList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  // biome-ignore lint/style/noNonNullAssertion: <its fine here>
  const defaultStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0]!;
  // biome-ignore lint/style/noNonNullAssertion: <its fine here>
  const defaultEndDate = new Date().toISOString().split("T")[0]!;
  const [dateRange, setDateRange] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  });
  const [stampId, setStampId] = useState<number | undefined>();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: LEADS_PER_PAGE,
  });
  const prevFiltersRef = useRef({
    searchTerm: "",
    startDate: "",
    endDate: "",
    stampId,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const leadIds = useMemo(() => {
    if (!debouncedSearchTerm) {
      return;
    }
    const ids = debouncedSearchTerm
      .split(leadIdRegex)
      .map((id) => Number.parseInt(id.trim(), 10))
      .filter((id) => !Number.isNaN(id));
    return ids.length > 0 ? ids : undefined;
  }, [debouncedSearchTerm]);

  const handlePreviousPage = () => {
    if (paginationData?.hasPrev) {
      setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }));
    }
  };

  const handleNextPage = () => {
    if (paginationData?.hasNext) {
      setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
    }
  };

  // Reset pagination when search term or date range changes
  useEffect(() => {
    const currentFilters = {
      searchTerm: debouncedSearchTerm,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      stampId,
    };

    const prevFilters = prevFiltersRef.current;
    const hasFiltersChanged =
      prevFilters.searchTerm !== currentFilters.searchTerm ||
      prevFilters.startDate !== currentFilters.startDate ||
      prevFilters.endDate !== currentFilters.endDate ||
      prevFilters.stampId !== currentFilters.stampId;

    if (hasFiltersChanged) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      prevFiltersRef.current = currentFilters;
    }
  }, [debouncedSearchTerm, dateRange.startDate, dateRange.endDate, stampId]);

  const getLeadsPayload = {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    leadIds,
    page: pagination.pageIndex + 1, // Convert 0-based index to 1-based page
    limit: pagination.pageSize,
    stampId,
  };

  const {
    data,
    isLoading: loadingLeads,
    // error,
  } = useListLeads(getLeadsPayload);

  const leads = data?.data?.leads || [];
  const paginationData = data?.data?.pagination;

  const table = useReactTable({
    data: leads,
    columns,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true, // Enable server-side pagination
    manualFiltering: true,
    pageCount: paginationData?.totalPages ?? -1, // Tell the table how many pages there are
    state: {
      pagination,
    },
  });

  const { data: leadsTabData } = useGetLeadTabCount({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    page: pagination.pageIndex + 1, // Convert 0-based index to 1-based page
    limit: pagination.pageSize,
  });
  const tabs: { stampId: number; name: string; count?: string }[] = [
    ...(leadsTabData?.data || []).map((tab) => ({
      ...tab,
      count: tab.count?.toString(),
    })),
  ];

  // if (error) {
  //   return (
  //     <div className="flex items-center justify-center h-64">
  //       <div className="text-lg text-red-600">
  //         Error loading leads: {error.message}
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Lead Detail</h1>
        <div className="flex items-center space-x-4 relative z-10">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              className="pl-10 w-80"
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter multiple Lead ID's separately..."
              value={searchTerm}
            />
          </div>

          {/* Date Range */}
          <div className="z-10">
            <DateRangePicker
              align="end"
              initialDateFrom={dateRange.startDate}
              initialDateTo={dateRange.endDate}
              onUpdate={(values) => {
                setDateRange({
                  startDate: values.from
                    ? // biome-ignore lint/style/noNonNullAssertion: <ok here>
                      values.from.toISOString().split("T")[0]!
                    : defaultStartDate,
                  endDate: values.to
                    ? // biome-ignore lint/style/noNonNullAssertion: <ok here>
                      values.to.toISOString().split("T")[0]!
                    : "",
                });
              }}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 border-b">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            stampId
              ? "border-transparent text-gray-500 hover:text-gray-700"
              : "border-blue-500 text-blue-600"
          }`}
          key={"all"}
          onClick={() => setStampId(undefined)}
          type="button"
        >
          All
        </button>
        {tabs.map(
          (tab) =>
            tab && (
              <button
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  stampId === tab.stampId
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                key={tab.stampId}
                onClick={() => setStampId(tab.stampId)}
                type="button"
              >
                {tab.name} {tab.count && tab.count !== "0" && `(${tab.count})`}
              </button>
            ),
        )}
      </div>

      {/* Table */}
      {loadingLeads ? (
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <Loader className="h-8 w-8" />
          <div className="text-sm text-gray-500">Loading Leads...</div>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : (
                        <div>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </div>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    data-state={row.getIsSelected() && "selected"}
                    key={row.id}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    className="h-24 text-center"
                    colSpan={columns.length}
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Footer */}
      {paginationData && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {(paginationData.page - 1) * paginationData.limit + 1} to{" "}
            {Math.min(
              paginationData.page * paginationData.limit,
              paginationData.total,
            )}{" "}
            of {paginationData.total} leads
          </div>

          <div className="flex items-center space-x-2">
            <Button
              className="flex items-center space-x-1"
              disabled={!paginationData.hasPrev}
              onClick={handlePreviousPage}
              size="sm"
              variant="outline"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Previous</span>
            </Button>

            <div className="flex items-center space-x-1">
              <span className="text-sm">
                Page{" "}
                <strong>
                  {paginationData.page} of {paginationData.totalPages}
                </strong>
              </span>
            </div>

            <Button
              className="flex items-center space-x-1"
              disabled={!paginationData.hasNext}
              onClick={handleNextPage}
              size="sm"
              variant="outline"
            >
              <span>Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsList;
